import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { getFirestoreDb } from "@/infrastructure/firebase/config";

function getDb() {
  return getFirestoreDb();
}
import type { Notification } from "@/domain/notification/notification.types";
import type { NotificationRepository } from "@/repositories/NotificationRepository";

const COLLECTION = "notifications";

function toNotification(data: Record<string, unknown>, id: string): Notification {
  const createdAt = data.createdAt instanceof Timestamp
    ? data.createdAt.toDate().toISOString()
    : typeof data.createdAt === "string"
      ? data.createdAt
      : new Date().toISOString();

  return {
    id,
    userId: data.userId as string,
    type: data.type as Notification["type"],
    taskId: (data.taskId as string) ?? null,
    title: data.title as string,
    message: data.message as string,
    read: data.read as boolean,
    createdAt,
  };
}

export class FirestoreNotificationRepository implements NotificationRepository {
  async getNotifications(userId: string): Promise<Notification[]> {
    const q = query(
      collection(getDb(), COLLECTION),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => toNotification(d.data(), d.id));
  }

  async getUnreadCount(userId: string): Promise<number> {
    const q = query(
      collection(getDb(), COLLECTION),
      where("userId", "==", userId),
      where("read", "==", false),
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  async markAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(getDb(), COLLECTION, notificationId), { read: true });
  }

  async markAllAsRead(userId: string): Promise<void> {
    const q = query(
      collection(getDb(), COLLECTION),
      where("userId", "==", userId),
      where("read", "==", false),
    );
    const snapshot = await getDocs(q);
    const batch = writeBatch(getDb());
    snapshot.docs.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await deleteDoc(doc(getDb(), COLLECTION, notificationId));
  }

  subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const q = query(
      collection(getDb(), COLLECTION),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((d) => toNotification(d.data(), d.id));
        callback(notifications);
      },
      (error) => onError?.(error),
    );
  }
}
